import cn from 'classnames';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import AMPIFrameResizer from 'components/AMPIFrameResizer';
import useWidgetView from 'hooks/useWidgetView';
import { useDisplayUnits } from 'state/displayUnits/context';
import { useTracking, useTrackingState } from 'state/tracking';
import { WidgetRootProvider } from 'state/widgetRoot/context';
import { DataProperties, NumberOfUnits } from 'types';
import mergeRefs from 'utils/mergeRefs';
import styles from './Widget.module.scss';

export interface WidgetProps
  extends Partial<Omit<NumberOfUnits, 'expectedNumberOfUnits'>>,
    DataProperties {
  children?: React.ReactNode;
  className?: string;
  /**
   * called when the widget enters the viewport for the first time
   */
  onView?: () => void;
}

const Widget = React.forwardRef<HTMLElement, WidgetProps>(
  (
    {
      children,
      className,
      defaultNumberOfUnits,
      maxNumberOfUnits,
      onView,
      ...rest
    },
    ref,
  ) => {
    const [element, setElement] = useState<HTMLElement | null>(null);
    const { dispatch, displayUnits } = useDisplayUnits();
    const showWidget = displayUnits.length > 0;

    const { trackWidgetInViewport, trackWidgetLoad } = useTracking();
    const { isViewportTracked } = useTrackingState();

    const { ref: visibilityRef } = useWidgetView({
      disabled: !showWidget || isViewportTracked,
      onView() {
        trackWidgetInViewport();
        onView?.();
      },
    });

    useLayoutEffect(() => {
      dispatch({
        type: 'DISPLAY_UNITS_FILTER',
        payload: { defaultNumberOfUnits, maxNumberOfUnits },
      });
    }, [defaultNumberOfUnits, dispatch, maxNumberOfUnits]);

    useEffect(() => {
      if (showWidget) {
        // trackWidgetLoad will ensure the event is only dispatched once
        trackWidgetLoad();
      }
    }, [showWidget, trackWidgetLoad]);

    // if mergeRefs isn't memoized, react-intersection-observer in useWidgetView
    // will enter an infinite render loop
    const mergedRefs = useMemo(
      () => mergeRefs([visibilityRef, ref, setElement]),
      [ref, visibilityRef],
    );

    return !showWidget ? null : (
      <div className={cn(styles.widget, className)} ref={mergedRefs} {...rest}>
        <WidgetRootProvider rootElement={element}>
          <AMPIFrameResizer />
          {children}
        </WidgetRootProvider>
      </div>
    );
  },
);

export default Widget;
